import React from 'react';

import { RadioButtonGroup, RadioButtonGroupStyle } from '@app/common/design-system/radio-button-group/RadioButtonGroup';
import DocumentUpload from '@app/containers/shared/DocumentUpload';
import Note, { NoteType } from '@app/common/components/Note';
import { NswTerminationReason, viewUploadedDocumentFromDocument } from '@app/config';
import * as Popover from '@radix-ui/react-popover';
import { FileForUpload } from '@app/containers/shared/DocumentDrop';
import { DocumentDetails } from '@app/containers/shared/DocumentUpload';
import SupportingDocumentFullCriteria from './SupportingDocumentFullCriteria';

import styles from './NswNoticeSupportingDocuments.module.scss';
interface NswNoticeSupportingDocumentsProps {
    isSupportingDocumentRequired: boolean;
    isDisabled?: boolean;
    willUploadDocument?: 'yes' | 'no';
    handleUpdateDocuments: (documents: (FileForUpload | DocumentDetails)[]) => void;
    documents: (FileForUpload | DocumentDetails)[];
    currentDocumentId: string;
    documentUploadProgress: number;
    selectedReason?: NswTerminationReason;
    isUploadingDocuments: boolean;
    location: string;
    uploadError?: string;
}

const NswNoticeSupportingDocuments: React.FC<NswNoticeSupportingDocumentsProps> = ({
    isSupportingDocumentRequired,
    isDisabled,
    willUploadDocument,
    handleUpdateDocuments,
    currentDocumentId,
    documents,
    documentUploadProgress,
    selectedReason,
    isUploadingDocuments,
    location,
    uploadError
}) => {
    return (
        <div className={styles.container}>
            <RadioButtonGroup
                name="willUploadDocument"
                options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                ]}
                required
                disabled={isDisabled}
                label="Do you want to upload supporting document(s) with this notice?"
                radioGroupStyle={RadioButtonGroupStyle.SEPARATED}
                validateOnTouch={false}
            />
            {willUploadDocument === 'yes' && (
                <Popover.Root open={isSupportingDocumentRequired} defaultOpen={isSupportingDocumentRequired}>
                    <Popover.Portal>
                        <Popover.Content
                            side="right"
                            align="start"
                            hideWhenDetached
                            sideOffset={88}
                            collisionPadding={16}
                            avoidCollisions
                            className={styles.popoverContent}
                        >
                            <SupportingDocumentFullCriteria reasonForTermination={selectedReason} />
                        </Popover.Content>
                    </Popover.Portal>
                    <Popover.Anchor>
                        <DocumentUpload
                            lease={{ location }}
                            uploading={isUploadingDocuments}
                            updateDocuments={handleUpdateDocuments}
                            documents={documents}
                            backendError={uploadError}
                            documentUploadProgress={documentUploadProgress}
                            hideFileCategory={true}
                            disabled={isDisabled}
                            viewDocument={docId => viewUploadedDocumentFromDocument(currentDocumentId, docId)}
                            maxTotalSize={100}
                            maxFileSize={100}
                        />
                    </Popover.Anchor>
                </Popover.Root>
            )}
            {willUploadDocument === 'no' && isSupportingDocumentRequired && (
                <Note
                    title="You are required to provide evidence based on your Termination reason."
                    type={NoteType.INFO}
                >
                    If you have already provided it to the Tenant(s) before the Notice Date, please ignore this message.
                </Note>
            )}
        </div>
    );
};

export default NswNoticeSupportingDocuments;
